import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { getAppRoute, RoutesPath } from '../../../core/routes';
import { useFetchSearchResult, useSearchFoundDispatch } from '../../../apis/search/search';
import { type SearchResult } from '../../../apis/search/search.types';
import { useSelectFeatureSettings, useSelectIsRW } from '../../../apis/startup/startup.selectors';
import { SearchBarVp } from '../../shared/Searches/SearchBar-VP';
import { LocaleDropdown } from './LocaleDropdown';
import { LogoutDropdown } from './LogoutDropdown';
import { StoresDropdown } from './StoresDropdown';
import { useLeaveInterventionFromBookingConfirmationModal } from '../../../pages/Bookings/booking-leave-intervention/useLeaveInterventionFromBookingConfirmationModal';
import { deleteCustomer } from '../../../store/customerSlice';
import { useAppDispatch } from '../../../store/store';
import { resetIntervention } from '../../../store/interventionSlice';
import { useHeaderSearchInput, useSearchNavigation } from './SearchNavigationContext';
import { formatAPICLink } from '../../../utils/utils';
import useToaster from '../../../utils/hooks/useToaster';
import { useScanSearch } from '../../../features/scan-search/useScanSearch';

import styles from './header.module.css';

const pathNames = {
  product: getAppRoute(RoutesPath.productPage),
  intervention: getAppRoute(RoutesPath.interventionPage),
  shippingGroup: getAppRoute(RoutesPath.shippingGroupPage),
};

const Header = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isHomePage = [getAppRoute(RoutesPath.homePage)].includes(pathname);
  const isRW = useSelectIsRW();
  const { handleProductFound, handleInterventionFound } = useSearchFoundDispatch();
  const { result, isRequestPending, isRequestFailed, fetchResult, resetRequest } = useFetchSearchResult({ requestType: 'search' });
  const { t } = useTranslation();
  const { searchInput, setSearchInput } = useHeaderSearchInput();
  const { onScan } = useScanSearch({ isActive: isHomePage && isRW });
  const { LeaveInterventionFromBookingConfirmationModal, confirmLeaveInterventionFromBookingConfirmationModal } =
    useLeaveInterventionFromBookingConfirmationModal();
  const dispatch = useAppDispatch();
  const { addErrorToast } = useToaster();
  const isCatalogProductSheetActive = useSelectFeatureSettings('activationCatalogProductSheet');
  const { setPendingProductSheetId } = useSearchNavigation();

  const handleSearch = async (input: string) => {
    const leavingCancelled = !(await confirmLeaveInterventionFromBookingConfirmationModal());
    if (leavingCancelled) {
      return;
    }
    if (pathname === getAppRoute(RoutesPath.interventionPage, input)) {
      location.reload();
    } else {
      fetchResult(input);
    }
  };

  const handleProductResult = (searchResult: SearchResult) => {
    if (isCatalogProductSheetActive && searchResult.catalogProduct) {
      handleProductFound(searchResult.catalogProduct);
      setPendingProductSheetId(String(searchResult.catalogProduct.product.id));
      return;
    }

    if (searchResult.catalogProduct) {
      handleProductFound(searchResult.catalogProduct);
      navigate(getAppRoute(RoutesPath.productPage, String(searchResult.catalogProduct.product.id)));
    }

    dispatch(deleteCustomer());
    dispatch(resetIntervention(false));
  };

  const handleOtherResult = (searchResult: SearchResult) => {
    if (searchResult.type === 'intervention' && searchResult.value) {
      handleInterventionFound(searchResult.value);
    }

    const newPathname = [pathNames[searchResult.type], searchResult.value].filter(Boolean).join('/');
    navigate(newPathname);
  };

  onScan((code: string) => {
    setSearchInput(code);
    void handleSearch(code).finally(() => setSearchInput(''));
  });

  useEffect(() => {
    if (isRequestPending) return;

    const handleResult = () => {
      if (isRequestFailed) {
        addErrorToast(`${t('error.message.product.scan.failed.title')}.\n${t('error.message.product.scan.failed.description')}`);
      }

      if (result) {
        if (result.type === 'product') {
          handleProductResult(result);
        } else {
          handleOtherResult(result);
        }
      }

      resetRequest();
    };

    handleResult();
  }, [isRequestPending]);

  const shouldUseWhiteBackground =
    pathname.includes(getAppRoute(RoutesPath.sparePartOrders)) || pathname.includes(getAppRoute(RoutesPath.customerPage));

  return (
    <div className={clsx(styles.newToolbarContainer, { [styles.white]: shouldUseWhiteBackground })}>
      <div className={styles.toolbarSearch}>
        <SearchBarVp
          placeholder={t('toolbar.top.search.placeholder')}
          onSearch={handleSearch}
          value={searchInput}
          onChange={event => setSearchInput(formatAPICLink(event.target.value.replace(/\s/g, '')))}
          isWhiteBackground={isHomePage}
          data-testid="360-search-bar"
        />
      </div>

      <div className={styles.dropdownsContainer}>
        <StoresDropdown />
        <LogoutDropdown />
        <LocaleDropdown />
      </div>
      <LeaveInterventionFromBookingConfirmationModal />
    </div>
  );
};

export default Header;
